import React, { useMemo } from 'react';
import type { Block, ContentPageBlockContent, PlanVanAanpakBlockContent } from '@/types';
import { BlockType } from '@/types';
import { useContentPages } from '@/hooks/useContentPages';
import { buildContentPageCss } from './blocks/ContentPageBlock';
import { buildPlanVanAanpakCss } from './blocks/PlanVanAanpakBlock';
import { useQuotePageBrand } from '@/store/quoteClientLogoContext';

interface HtmlDescriptor {
  html: string;
  cssText: string;
  contentKey: string;
  brand: 'ubo' | 'ubx';
}

function extractHtml(block: Block, pageBrand: 'ubo' | 'ubx'): HtmlDescriptor | null {
  switch (block.type) {
    case BlockType.CONTENT_PAGE: {
      const c = block.content as ContentPageBlockContent;
      const brand = (c.theme ?? pageBrand) === 'ubx' ? 'ubx' : 'ubo';
      return {
        html: c.content || '',
        cssText: buildContentPageCss(block.id, c.style || 'default', brand),
        contentKey: 'content',
        brand,
      };
    }
    case BlockType.PLAN_VAN_AANPAK: {
      const c = block.content as PlanVanAanpakBlockContent;
      const brand = (c.theme ?? pageBrand) === 'ubx' ? 'ubx' : 'ubo';
      return {
        html: c.content || '',
        cssText: buildPlanVanAanpakCss(block.id),
        contentKey: 'content',
        brand,
      };
    }
    default:
      return null;
  }
}

/** Fragment-info voor print/PDF page-breaks (laatste fragment van laatste blok: geen break-after). */
export type MultiPageFragmentMeta = {
  fragmentIndex: number;
  fragmentCount: number;
};

interface MultiPageBlockProps {
  block: Block;
  renderPage: (block: Block, pageKey: string, meta?: MultiPageFragmentMeta) => React.ReactNode;
  /**
   * false = geen A4-overflowsplits op de gepubliceerde pagina; één doorlopende kaart per blok.
   * true (default) = bestaand gedrag (contentpagina’s kunnen meerdere “pagina’s” worden).
   */
  splitOverflow?: boolean;
  /** Alleen bij splitOverflow + HTML-blokken; na `useContentPages`-meting (voor Puppeteer). */
  onSplitMeasureComplete?: () => void;
  /** Alleen zinvol bij splitOverflow; zie `useContentPages` (publieke print/PDF). */
  mergeShortTrailingFragment?: boolean;
  /** Alleen bij splitOverflow; `pageFit` = nettere PDF-paginering. */
  pageBreakStrategy?: 'tiptap' | 'pageFit';
}

/**
 * Alleen gemount wanneer splitOverflow true — hooks-regels (useContentPages).
 */
function MultiPageBlockSplit({
  block,
  renderPage,
  descriptor,
  onSplitMeasureComplete,
  mergeShortTrailingFragment,
  pageBreakStrategy,
}: {
  block: Block;
  renderPage: (block: Block, pageKey: string, meta?: MultiPageFragmentMeta) => React.ReactNode;
  descriptor: HtmlDescriptor;
  onSplitMeasureComplete?: () => void;
  mergeShortTrailingFragment?: boolean;
  pageBreakStrategy?: 'tiptap' | 'pageFit';
}) {
  const html = descriptor.html || '';
  const cssText = descriptor.cssText;

  const pages = useContentPages(html, cssText, {
    brand: descriptor.brand,
    onMeasureComplete: onSplitMeasureComplete,
    mergeShortTrailingFragment,
    pageBreakStrategy,
  });

  if (pages.length <= 1) {
    // Eén pagina kan toch afwijken: een afbeelding is dan verkleind om te passen.
    const pageBlock: Block =
      pages[0] === undefined || pages[0] === html
        ? block
        : { ...block, content: { ...block.content, [descriptor.contentKey]: pages[0] } };
    return (
      <>{renderPage(pageBlock, block.id, { fragmentIndex: 0, fragmentCount: 1 })}</>
    );
  }

  return (
    <>
      {pages.map((pageHtml, idx) => {
        const pageBlock: Block = {
          ...block,
          content: {
            ...block.content,
            [descriptor.contentKey]: pageHtml,
          },
        };
        const pageKey = `${block.id}-overflow-${idx}`;
        const meta: MultiPageFragmentMeta = {
          fragmentIndex: idx,
          fragmentCount: pages.length,
        };
        return (
          <React.Fragment key={pageKey}>
            {renderPage(pageBlock, pageKey, meta)}
          </React.Fragment>
        );
      })}
    </>
  );
}

/**
 * Generic wrapper that handles overflow splitting for any block type
 * that contains HTML content (CONTENT_PAGE, PLAN_VAN_AANPAK, etc.).
 */
function MultiPageBlock({
  block,
  renderPage,
  splitOverflow = true,
  onSplitMeasureComplete,
  mergeShortTrailingFragment,
  pageBreakStrategy,
}: MultiPageBlockProps) {
  const quoteBrand = useQuotePageBrand();
  const descriptor = useMemo(() => extractHtml(block, quoteBrand), [block, quoteBrand]);

  if (!splitOverflow || !descriptor) {
    return (
      <>{renderPage(block, block.id, { fragmentIndex: 0, fragmentCount: 1 })}</>
    );
  }

  return (
    <MultiPageBlockSplit
      block={block}
      renderPage={renderPage}
      descriptor={descriptor}
      onSplitMeasureComplete={onSplitMeasureComplete}
      mergeShortTrailingFragment={mergeShortTrailingFragment}
      pageBreakStrategy={pageBreakStrategy}
    />
  );
}

export default MultiPageBlock;
